/**
 * Enhanced CSV Parser för skattekontodata och annan finansiell data
 * Hanterar parsing, validering, sanitering och strukturering av CSV-filer
 */

export interface ParsedCSVData {
  headers: string[];
  rows: any[];
  totalRows: number;
  validRows: number;
  errors: string[];
  delimiter: string;
  encoding: string;
}

/**
 * Normaliserar ett header-namn för fuzzy matching
 * - Trimmar whitespace
 * - Ersätter NBSP och andra icke-breaking spaces
 * - Tar bort citattecken
 * - NFD-normaliserar och tar bort diakritik
 * - toLowerCase för case-insensitive jämförelser
 */
export function normalizeHeaderName(header: string): string {
  if (!header || typeof header !== 'string') return '';
  
  return header
    .trim()
    .replace(/[\u00A0\u2009\u202F]/g, ' ') // NBSP → space
    .replace(/[""'']/g, '') // Ta bort citattecken
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '') // Ta bort diakritik (åäö → aao)
    .toLowerCase();
}

export interface CSVParseOptions {
  delimiter?: string;
  encoding?: string;
  skipEmptyLines?: boolean;
  maxRows?: number; // För att begränsa storleken
}

export interface CSVSanitizeOptions {
  removeBOM?: boolean;
  normalizeWhitespace?: boolean;
  parseSwedishNumbers?: boolean;
  parseSwedishDates?: boolean;
}

/**
 * Sanitize CSV content - remove BOM, normalize whitespace, handle Swedish formats
 */
export function sanitizeCSVContent(content: string, options: CSVSanitizeOptions = {}): string {
  const {
    removeBOM = true,
    normalizeWhitespace = true,
    parseSwedishNumbers = false,
    parseSwedishDates = false,
  } = options;

  let sanitized = content;

  // Remove BOM (Byte Order Mark)
  if (removeBOM) {
    sanitized = sanitized.replace(/^\uFEFF/, '');
  }

  // Normalize line endings
  sanitized = sanitized.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Handle non-breaking spaces and normalize whitespace
  if (normalizeWhitespace) {
    const lines = sanitized.split('\n').map(line => {
      // Replace NBSP and thin spaces with regular space, but preserve delimiters (tabs/semicolons/commas)
      line = line.replace(/[\u00A0\u2009\u202F]/g, ' ');
      // Only trim leading/trailing spaces – do NOT collapse internal whitespace to avoid breaking TSV/CSV structure
      return line.trim();
    });
    sanitized = lines.join('\n');
  }

  return sanitized;
}

/**
 * Auto-detect CSV delimiter from content
 */
export function sniffCSVDelimiter(content: string): string {
  const lines = content.split('\n').slice(0, 5);
  const delimiters = [';', ',', '\t'];

  // Räkna genomsnittligt antal kolumner per kandidat
  let bestDelimiter = ';';
  let maxAvgColumns = 0;

  for (const delimiter of delimiters) {
    const cols = lines.map(l => (l ? l.split(delimiter).length : 0));
    const avg = cols.reduce((a, b) => a + b, 0) / Math.max(cols.length, 1);
    if (avg > maxAvgColumns) {
      maxAvgColumns = avg;
      bestDelimiter = delimiter;
    }
  }

  // Om ingen kandidat ger >1 kolumn i snitt, prova whitespace-baserad split (2+ spaces eller tab)
  if (maxAvgColumns < 2) {
    const whitespaceCols = lines.map(l => (l ? l.trim().split(/[\s\u00A0\u2009\u202F]{2,}|\t/).length : 0));
    const whitespaceAvg = whitespaceCols.reduce((a, b) => a + b, 0) / Math.max(whitespaceCols.length, 1);
    if (whitespaceAvg >= 2) return 'WHITESPACE';
  }

  return bestDelimiter;
}

/**
 * Normalize Swedish number format (12 345,67 -> 12345.67)
 * Hanterar: NBSP, vanliga spaces, "SEK" suffix, unicode minus (− \u2212), parenteser
 * FÖRBÄTTRAD: Mer robust hantering av tusentals-separatorer och decimalformat
 */
export function normalizeSwedishNumber(value: string): number | null {
  if (!value || typeof value !== 'string') return null;
  
  let s = value.trim();

  // Negativa tal i parentes: (1000) -> -1000
  const paren = s.match(/^\(([^)]+)\)$/);
  if (paren) s = '-' + paren[1].trim();

  // Ta bort valutor/suffix
  s = s.replace(/\s*(SEK|kr|:-|€|EUR|USD|GBP)\s*$/gi, '');

  // Hantera format som "1 234,-" och trailing minus
  s = s.replace(/,-$/, ',');
  const hasTrailingMinus = /-$/.test(s);
  if (hasTrailingMinus) s = s.slice(0, -1);

  // Normalisera minustecken (unicode → '-')
  s = s.replace(/[\u2212−–—]/g, '-');

  // Ta bort alla whitespace-varianter
  s = s.replace(/[\s\u00A0\u2009\u202F]+/g, '');

  // Ta bort apostrof som tusentalsavskiljare
  s = s.replace(/'/g, '');

  // Hantera blandade tusentals-/decimaltecken
  const dotCount = (s.match(/\./g) || []).length;
  const commaCount = (s.match(/,/g) || []).length;

  if (commaCount > 0 && dotCount > 0) {
    // T.ex. 1.234,56 -> 1234.56
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (commaCount > 0) {
    // T.ex. 1234,56 -> 1234.56
    s = s.replace(',', '.');
  } else if (dotCount > 1) {
    // T.ex. 1.234.567.89 -> behåll sista punkt som decimal, ta bort övriga
    const last = s.lastIndexOf('.');
    s = s.slice(0, last).replace(/\./g, '') + '.' + s.slice(last + 1);
  }

  if (hasTrailingMinus && !s.startsWith('-')) s = '-' + s;

  const num = parseFloat(s);
  if (!isNaN(num)) return num;

  // Fallback: extrahera första talmönster och normalisera igen
  const m = value.match(/-?\(?[\d\s\u00A0\u2009\u202F.,']+(?:[.,]\d+)?\)?-?/);
  if (m) {
    let t = m[0]
      .replace(/[\u2212−–—]/g, '-')
      .replace(/[\s\u00A0\u2009\u202F']/g, '');
    const trailing = /-$/.test(t);
    if (trailing) t = t.slice(0, -1);
    if (t.startsWith('(') && t.endsWith(')')) t = '-' + t.slice(1, -1);

    const d = (t.match(/\./g) || []).length;
    const c = (t.match(/,/g) || []).length;
    if (c > 0 && d > 0) t = t.replace(/\./g, '').replace(',', '.');
    else if (c > 0) t = t.replace(',', '.');
    else if (d > 1) {
      const last = t.lastIndexOf('.');
      t = t.slice(0, last).replace(/\./g, '') + '.' + t.slice(last + 1);
    }

    if (trailing && !t.startsWith('-')) t = '-' + t;

    const parsed = parseFloat(t);
    if (!isNaN(parsed)) return parsed;
  }

  return null;
}

/**
 * Parse Swedish date formats to ISO format
 * FÖRBÄTTRAD: Hanterar även Excel-serienummer
 */
export function normalizeSwedishDate(value: string): string | null {
  if (!value || typeof value !== 'string') return null;
  
  let s = value.trim();

  // Ta bort trailing punkt/komma
  s = s.replace(/[.,]$/,'');

  // Strip time component like "YYYY-MM-DD HH:MM:SS" or ISO with T
  if (/^\d{4}-\d{1,2}-\d{1,2}[ T]/.test(s)) s = s.split(/[ T]/)[0];

  // Excel serienummer (bredare intervall)
  const asNum = Number(s);
  if (!isNaN(asNum) && asNum >= 35000 && asNum <= 70000) {
    const base = Date.UTC(1899, 11, 30);
    const d = new Date(base + Math.round(asNum) * 86400000);
    return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
  }

  // YYYYMMDD
  if (/^\d{8}$/.test(s)) {
    const y = s.slice(0, 4), m = s.slice(4, 6), d = s.slice(6, 8);
    const dt = new Date(`${y}-${m}-${d}T00:00:00Z`);
    return isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10);
  }

  // YYYY-M-D (tillåt 1-siffriga månad/dag)
  let m = s.match(/^(\d{4})[\/.\-](\d{1,2})[\/.\-](\d{1,2})$/);
  if (m) {
    const y = m[1], mm = m[2].padStart(2,'0'), dd = m[3].padStart(2,'0');
    const dt = new Date(`${y}-${mm}-${dd}T00:00:00Z`);
    return isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10);
  }

  // D/M/YYYY eller D.M.YYYY eller D-M-YYYY (tillåt 1-2 siffror)
  m = s.match(/^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{4})$/);
  if (m) {
    const dd = m[1].padStart(2,'0'), mm = m[2].padStart(2,'0'), y = m[3];
    const dt = new Date(`${y}-${mm}-${dd}T00:00:00Z`);
    return isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10);
  }

  // Svenska månadsnamn: "1 jan 2024", "01 januari 2024"
  const months: Record<string, number> = {
    'jan':1,'januari':1,
    'feb':2,'februari':2,
    'mar':3,'mars':3,
    'apr':4,'april':4,
    'maj':5,
    'jun':6,'juni':6,
    'jul':7,'juli':7,
    'aug':8,'augusti':8,
    'sep':9,'sept':9,'september':9,
    'okt':10,'oktober':10,
    'nov':11,'november':11,
    'dec':12,'december':12
  };
  const nameMatch = s.toLowerCase().match(/^(\d{1,2})\s*([a-zåäö]+)\s*(\d{4})$/i);
  if (nameMatch) {
    const dd = nameMatch[1].padStart(2,'0');
    const monKey = nameMatch[2].toLowerCase();
    const yyyy = nameMatch[3];
    const mmNum = months[monKey];
    if (mmNum) {
      const mm = String(mmNum).padStart(2,'0');
      const dt = new Date(`${yyyy}-${mm}-${dd}T00:00:00Z`);
      return isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10);
    }
  }

  // ISO YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10);
}

/**
 * Parsa CSV-fil till strukturerad data
 */
export async function parseCSVFile(
  file: File, 
  options: CSVParseOptions = {}
): Promise<ParsedCSVData> {
  const {
    delimiter,
    encoding = 'utf-8',
    skipEmptyLines = true,
    maxRows = 10000 // Historisk standardbegränsning; faktisk batchning styrs av token-budget (se audit-full-analysis/config.ts)
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        
        // Sanitize the content first
        const sanitized = sanitizeCSVContent(text);
        
        // Auto-detect delimiter if not provided
        const detectedDelimiter = delimiter || sniffCSVDelimiter(sanitized);
        
        const result = parseCSVText(sanitized, { 
          delimiter: detectedDelimiter, 
          skipEmptyLines, 
          maxRows 
        });
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Kunde inte läsa filen'));
    reader.readAsText(file, encoding);
  });
}

/**
 * Parsa CSV-text till strukturerad data
 */
export function parseCSVText(
  text: string, 
  options: Pick<CSVParseOptions, 'delimiter' | 'skipEmptyLines' | 'maxRows'> = {}
): ParsedCSVData {
  const { delimiter = ';', skipEmptyLines = true, maxRows = 10000 } = options;
  
  const lines = text.split(/\r?\n/);
  const errors: string[] = [];
  
  if (lines.length === 0) {
    throw new Error('CSV-filen är tom');
  }

  // Parse headers from first line and normalize them for consistent key matching
  const headerLine = lines[0];
  const rawHeaders = (delimiter === 'WHITESPACE'
    ? headerLine.trim().split(/[\s\u00A0\u2009\u202F]{2,}|\t/)
    : parseCSVLine(headerLine, delimiter)
  );
  
  // ROBUST HEADER NORMALIZATION: Trim, remove NBSP, quotes
  const headers = rawHeaders.map(h => 
    h.trim().replace(/[\u00A0\u2009\u202F]/g, ' ').replace(/[""'']/g, '').trim()
  );
  
  if (headers.length === 0) {
    throw new Error('Inga kolumnrubriker hittades');
  }

  // Parse data rows
  const rows: any[] = [];
  let validRows = 0;
  let processedRows = 0;

  for (let i = 1; i < lines.length && processedRows < maxRows; i++) {
    const line = lines[i].trim();
    
    if (skipEmptyLines && line === '') continue;
    
    try {
      const values = (delimiter === 'WHITESPACE'
        ? line.split(/[\s\u00A0\u2009\u202F]{2,}|\t/)
        : parseCSVLine(line, delimiter)
      );
      
      if (values.length !== headers.length) {
        errors.push(`Rad ${i + 1}: Antal kolumner (${values.length}) matchar inte rubrikerna (${headers.length})`);
        continue;
      }

      // Skapa objekt från headers och values
      const rowObj: any = {};
      headers.forEach((header, index) => {
        rowObj[header.trim()] = values[index]?.trim() || '';
      });

      rows.push(rowObj);
      validRows++;
      processedRows++;
      
    } catch (error: any) {
      errors.push(`Rad ${i + 1}: ${error.message}`);
    }
  }

  if (processedRows >= maxRows) {
    errors.push(`Filstorlek begränsad till ${maxRows} rader för att kontrollera kostnader`);
  }

  return {
    headers,
    rows,
    totalRows: lines.length - 1, // Exklusive header
    validRows,
    errors,
    delimiter,
    encoding: 'utf-8'
  };
}

/**
 * Parse CSV text with an explicit dialect
 * - Supports headerless files by generating synthetic headers "Kolumn N"
 */
export function parseCSVTextWithDialect(
  text: string,
  dialect: { delimiter: string; hasHeader: boolean; skipEmptyLines?: boolean; maxRows?: number }
): ParsedCSVData {
  const sanitized = sanitizeCSVContent(text);
  const delimiter = dialect.delimiter || ';';
  const skipEmptyLines = dialect.skipEmptyLines ?? true;
  const maxRows = dialect.maxRows ?? 10000;

  const lines = sanitized.split(/\r?\n/);
  const errors: string[] = [];

  const nextNonEmptyLineIndex = (start: number) => {
    for (let i = start; i < lines.length; i++) {
      const l = lines[i].trim();
      if (!skipEmptyLines || l !== '') return i;
    }
    return -1;
  };

  if (dialect.hasHeader) {
    // Delegera till befintlig parser men med exakt delimiter
    return parseCSVText(sanitized, { delimiter, skipEmptyLines, maxRows });
  }

  // Headerless: skapa syntetiska headers baserat på första icke-tomma raden
  const firstIdx = nextNonEmptyLineIndex(0);
  if (firstIdx === -1) {
    throw new Error('CSV-filen är tom');
  }

  const splitLine = (line: string): string[] => {
    if (delimiter === 'WHITESPACE') {
      return line.trim().split(/[\s\u00A0\u2009\u202F]{2,}|\t/).map(v => v.trim());
    }
    return parseCSVLine(line, delimiter);
  };

  const firstValues = splitLine(lines[firstIdx]);
  const numCols = firstValues.length;
  const headers = Array.from({ length: numCols }, (_, i) => `Kolumn ${i + 1}`);

  const rows: any[] = [];
  let validRows = 0;
  let processedRows = 0;

  for (let i = firstIdx; i < lines.length && processedRows < maxRows; i++) {
    const raw = lines[i];
    const line = raw.trim();
    if (skipEmptyLines && line === '') continue;

    const values = splitLine(raw);
    // Justera antal värden till numCols
    const adjusted = values.slice(0, numCols);
    while (adjusted.length < numCols) adjusted.push('');

    const obj: any = {};
    headers.forEach((h, idx) => {
      obj[h] = (adjusted[idx] ?? '').toString().trim();
    });

    rows.push(obj);
    validRows++;
    processedRows++;
  }

  return {
    headers,
    rows,
    totalRows: rows.length,
    validRows,
    errors,
    delimiter,
    encoding: 'utf-8',
  };
}

/**
 * Parsa en CSV-rad med hänsyn till quoted values
 * UPPDATERAD: Trimmar värden inklusive NBSP
 */
function parseCSVLine(line: string, delimiter: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      // Field separator - trim value including NBSP
      values.push(current.trim().replace(/[\u00A0\u2009\u202F]/g, ' ').trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  // Add final value - trim including NBSP
  values.push(current.trim().replace(/[\u00A0\u2009\u202F]/g, ' ').trim());
  
  return values;
}

/**
 * Validera skattekontodata struktur
 */
export function validateTaxAccountData(data: any[]): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (data.length === 0) {
    errors.push('Ingen data att validera');
    return { isValid: false, errors };
  }

  // Kolla efter viktiga kolumner för skattekonto
  const firstRow = data[0];
  const requiredFields = ['Datum', 'Belopp', 'Beskrivning'];
  const recommendedFields = ['Konto', 'Kontonamn', 'Verifikat'];
  
  const availableFields = Object.keys(firstRow);
  
  // Kontrollera obligatoriska fält (flexibelt)
  const missingRequired = requiredFields.filter(field => 
    !availableFields.some(available => 
      available.toLowerCase().includes(field.toLowerCase())
    )
  );

  if (missingRequired.length > 0) {
    errors.push(`Saknade obligatoriska kolumner: ${missingRequired.join(', ')}`);
  }

  // Varning för rekommenderade fält
  const missingRecommended = recommendedFields.filter(field => 
    !availableFields.some(available => 
      available.toLowerCase().includes(field.toLowerCase())
    )
  );

  if (missingRecommended.length > 0) {
    errors.push(`Rekommenderade kolumner saknas: ${missingRecommended.join(', ')}`);
  }

  // Validera datatyper i sample
  const sampleSize = Math.min(10, data.length);
  for (let i = 0; i < sampleSize; i++) {
    const row = data[i];
    
    // Leta efter datumkolumn
    const dateField = availableFields.find(field => 
      field.toLowerCase().includes('datum')
    );
    
    if (dateField && row[dateField]) {
      const dateValue = row[dateField];
      if (dateValue && !isValidDate(dateValue)) {
        errors.push(`Rad ${i + 1}: Ogiltigt datumformat i "${dateField}": ${dateValue}`);
      }
    }
    
    // Leta efter beloppkolumn
    const amountField = availableFields.find(field => 
      field.toLowerCase().includes('belopp') || 
      field.toLowerCase().includes('summa')
    );
    
    if (amountField && row[amountField]) {
      const amountValue = row[amountField];
      if (amountValue && !isValidAmount(amountValue)) {
        errors.push(`Rad ${i + 1}: Ogiltigt beloppsformat i "${amountField}": ${amountValue}`);
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validera datumformat (flexibelt)
 */
function isValidDate(value: string): boolean {
  if (!value) return false;
  
  // Olika format som kan vara giltiga
  const dateFormats = [
    /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
    /^\d{2}\/\d{2}\/\d{4}$/, // DD/MM/YYYY
    /^\d{2}-\d{2}-\d{4}$/, // DD-MM-YYYY
    /^\d{8}$/ // YYYYMMDD
  ];
  
  if (!dateFormats.some(format => format.test(value))) {
    return false;
  }
  
  // Testa om det kan parsas som datum
  const date = new Date(value);
  return !isNaN(date.getTime());
}

/**
 * Validera beloppformat
 */
function isValidAmount(value: string): boolean {
  if (!value) return false;
  
  // Ta bort whitespace och vanliga separatorer
  const cleaned = value.replace(/[\s,]/g, '').replace(',', '.');
  
  // Kontrollera om det är ett giltigt nummer
  const num = parseFloat(cleaned);
  return !isNaN(num);
}

/**
 * Transformera data för AI-analys
 */
export function transformDataForAnalysis(data: any[]): any[] {
  return data.map((row, index) => ({
    id: `row_${index}`,
    originalData: row,
    // Normalisera vanliga fält
    date: extractDateField(row),
    amount: extractAmountField(row),
    description: extractDescriptionField(row),
    account: extractAccountField(row),
    reference: extractReferenceField(row)
  }));
}

function extractDateField(row: any): string | null {
  const dateKeys = Object.keys(row).filter(key => 
    key.toLowerCase().includes('datum')
  );
  return dateKeys.length > 0 ? row[dateKeys[0]] : null;
}

function extractAmountField(row: any): number | null {
  const amountKeys = Object.keys(row).filter(key => 
    key.toLowerCase().includes('belopp') || 
    key.toLowerCase().includes('summa')
  );
  
  if (amountKeys.length === 0) return null;
  
  const value = row[amountKeys[0]];
  if (!value) return null;
  
  const cleaned = value.toString().replace(/[\s,]/g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

function extractDescriptionField(row: any): string | null {
  const descKeys = Object.keys(row).filter(key => 
    key.toLowerCase().includes('beskrivning') ||
    key.toLowerCase().includes('text') ||
    key.toLowerCase().includes('kommentar')
  );
  return descKeys.length > 0 ? row[descKeys[0]] : null;
}

function extractAccountField(row: any): string | null {
  const accountKeys = Object.keys(row).filter(key => 
    key.toLowerCase().includes('konto')
  );
  return accountKeys.length > 0 ? row[accountKeys[0]] : null;
}

function extractReferenceField(row: any): string | null {
  const refKeys = Object.keys(row).filter(key => 
    key.toLowerCase().includes('verifikat') ||
    key.toLowerCase().includes('referens') ||
    key.toLowerCase().includes('id')
  );
  return refKeys.length > 0 ? row[refKeys[0]] : null;
}